/**
 * Label — form label primitive.
 *
 * Design system mapping:
 *   • Font:    text-sm font-normal (matches Webex form labels: 14/400)
 *   • Color:   text-webex-gray-950 dark:text-webex-gray-50
 *   • Required indicator: webex-red-600 asterisk after label text
 *
 * Pairs with any form control via `htmlFor`. When wrapped in <Field.Root>,
 * htmlFor wiring happens automatically through Base UI Field.
 */
import * as React from "react"
import { cn } from "@lib/cn"

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean
}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, children, required, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn(
          "text-sm font-normal leading-tight text-webex-gray-950 dark:text-webex-gray-50 select-none",
          className,
        )}
        {...props}
      >
        {children}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-webex-red-600">
            *
          </span>
        )}
      </label>
    )
  },
)
Label.displayName = "Label"
